/**
 * All Stores.
 *
 * The full Vaperite estate — the flat list this page has always shown, now with
 * a status column so the branches trading with Deeliver stand out from the rest.
 *
 * Deeliver's own Diep River location is not in here: it isn't a Vaperite branch.
 */

import { useMemo, useState } from 'react';
import {
  Box, Paper, TextField, InputAdornment, Tooltip, IconButton, Chip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import { useAdminTheme } from '../../admin-theme';
import { VAPERITE_STORES, type VaperiteStore } from '../../data/vaperiteStores';

const UBER_GREEN = '#06C167';

export const AllStoresTable = () => {
  const { colors, cardStyle } = useAdminTheme();
  const [search, setSearch] = useState('');

  const stores = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return VAPERITE_STORES;
    return VAPERITE_STORES.filter((s) =>
      [s.branch, s.address, s.city, s.province, s.zip, s.phone, s.email]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [search]);

  const headCell = {
    textAlign: 'left' as const,
    padding: '12px 16px',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    color: colors.textMuted,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap' as const,
  };

  const cell = {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textPrimary,
    fontSize: '0.85rem',
    verticalAlign: 'middle' as const,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ color: colors.textSecondary }}>
          {stores.length} of {VAPERITE_STORES.length} branches
        </Typography>
        <TextField
          size="small"
          placeholder="Search branch, city, phone or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.textMuted, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 300,
            bgcolor: colors.bgElevated,
            '& .MuiInputBase-input': { color: colors.textPrimary },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
          }}
        />
      </Box>

      <Paper sx={{ ...cardStyle, borderRadius: 2, overflowX: 'auto', maxWidth: '100%' }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={headCell}>Branch</Box>
              <Box component="th" sx={headCell}>Status</Box>
              <Box component="th" sx={headCell}>Address</Box>
              <Box component="th" sx={headCell}>City</Box>
              <Box component="th" sx={headCell}>Province</Box>
              <Box component="th" sx={headCell}>Zip</Box>
              <Box component="th" sx={headCell}>Country</Box>
              <Box component="th" sx={headCell}>Phone</Box>
              <Box component="th" sx={headCell}>Email</Box>
              <Box component="th" sx={{ ...headCell, textAlign: 'right' }}>Links</Box>
            </Box>
          </Box>

          <Box component="tbody">
            {stores.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={10} sx={{ ...cell, textAlign: 'center', py: 6, color: colors.textMuted }}>
                  No branches match that search.
                </Box>
              </Box>
            ) : (
              stores.map((store: VaperiteStore) => (
                <Box component="tr" key={store.branch} sx={{ '&:hover': { bgcolor: colors.bgElevated } }}>
                  <Box component="td" sx={{ ...cell, fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {store.branch}
                  </Box>

                  <Box component="td" sx={cell}>
                    <Chip
                      size="small"
                      label={store.isActive ? 'Active' : 'Inactive'}
                      sx={{
                        fontSize: '0.7rem',
                        height: 22,
                        fontWeight: 700,
                        bgcolor: store.isActive ? `${UBER_GREEN}1f` : `${colors.textMuted}1f`,
                        color: store.isActive ? UBER_GREEN : colors.textMuted,
                      }}
                    />
                  </Box>

                  <Box component="td" sx={{ ...cell, color: colors.textSecondary, minWidth: 260 }}>
                    {store.address}
                  </Box>
                  <Box component="td" sx={{ ...cell, whiteSpace: 'nowrap' }}>{store.city}</Box>
                  <Box component="td" sx={cell}>
                    <Chip
                      size="small"
                      label={store.province}
                      sx={{ bgcolor: `${colors.info}1a`, color: colors.info, fontSize: '0.7rem', height: 22 }}
                    />
                  </Box>
                  <Box component="td" sx={{ ...cell, fontFamily: 'monospace', color: colors.textSecondary }}>
                    {store.zip}
                  </Box>
                  <Box component="td" sx={{ ...cell, color: colors.textSecondary, whiteSpace: 'nowrap' }}>
                    {store.country}
                  </Box>
                  {/* Not every branch has a contact on file — render a dash
                      rather than an empty link that goes nowhere. */}
                  <Box component="td" sx={{ ...cell, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {store.phone ? (
                      <Box component="a" href={`tel:${store.phone}`} sx={{ color: colors.textPrimary, textDecoration: 'none' }}>
                        {store.phone}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ color: colors.textMuted }}>—</Box>
                    )}
                  </Box>
                  <Box component="td" sx={{ ...cell, whiteSpace: 'nowrap' }}>
                    {store.email ? (
                      <Box component="a" href={`mailto:${store.email}`} sx={{ color: colors.info, textDecoration: 'none' }}>
                        {store.email}
                      </Box>
                    ) : (
                      <Box component="span" sx={{ color: colors.textMuted }}>—</Box>
                    )}
                  </Box>

                  {/* Storefront links. Uber Eats is only present for the branches
                      that are actually listed there. */}
                  <Box component="td" sx={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {store.link && (
                      <Tooltip title="Vaperite store page">
                        <IconButton
                          size="small"
                          component="a"
                          href={store.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: colors.textSecondary }}
                        >
                          <PublicIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {store.uberEatsLink && (
                      <Tooltip title="Uber Eats listing">
                        <IconButton
                          size="small"
                          component="a"
                          href={store.uberEatsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: UBER_GREEN }}
                        >
                          <PublicIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AllStoresTable;
